import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import "@/index.css";
// El fallback del ErrorBoundary se renderiza FUERA del árbol de React de la app,
// así que no hay `useTranslation` disponible. Se usa la instancia i18n directa,
// como ya hace `utils/tours.js`: `i18n.js` llama a `.init()` de forma síncrona con
// los recursos empaquetados, de modo que `t()` ya resuelve cuando se importa.
import i18n from "@/i18n";
import App from "@/App";
import { beforeSend } from "@/lib/filtroSentry";

// =====================================================================
// Harden DOM mutation against browser auto-translation crashes
// =====================================================================
// Browser translators (Google Translate, the built-in Chrome/Edge translate,
// some extensions) replace text nodes in place. When React later unmounts or
// moves those nodes (e.g. closing a Radix Select or switching a Tab), it calls
// removeChild/insertBefore on a node the translator already swapped, and the
// browser throws "NotFoundError: Failed to execute 'removeChild' on 'Node'".
// In React 19 that error escapes commitDeletionEffects and takes down the whole
// tree (surfaced in Sentry). We make these two operations no-throw when the
// node isn't actually a child, returning gracefully instead of crashing. This
// runs before any render so every mutation is covered.
// See the well-known "Google Translate + React removeChild" issue.
if (typeof Node === "function" && Node.prototype) {
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function (child) {
    if (child.parentNode !== this) {
      return child;
    }
    return originalRemoveChild.apply(this, arguments);
  };
  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function (newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      return newNode;
    }
    return originalInsertBefore.apply(this, arguments);
  };
}

// =====================================================================
// Sentry initialization
// =====================================================================
// Initialize as early as possible — before React renders — so errors thrown
// during the very first render are captured. When REACT_APP_SENTRY_DSN is
// empty the SDK init is skipped entirely and Sentry becomes a no-op.
//
// PII (cookies, request bodies, user info) is NOT sent by default. To attach
// the current user explicitly, call Sentry.setUser({...}) after login.
//
// Solo se inicializa en builds de PRODUCCIÓN. `npm start` lee el mismo .env, así
// que con solo `if (sentryDsn)` el dev server reportaba a Sentry: seis issues del
// dashboard salieron de archivos `*.hot-update.js` —que únicamente existen con el
// Hot Module Replacement del dev server— y eran errores transitorios de un archivo
// a medio editar, no fallos de nadie. Ensuciaban el tablero de producción y
// gastaban cuota. Misma palanca de escape que el backend (SENTRY_SEND_IN_DEV).
const sentryDsn = process.env.REACT_APP_SENTRY_DSN;
const isProdBuild = process.env.NODE_ENV === "production";
const sendInDev = process.env.REACT_APP_SENTRY_SEND_IN_DEV === "1";
// El build del ambiente dev (corepack yarn deploy:dev) apaga Sentry con esta
// variable en vez de vaciar el DSN: en Windows, asignarle "" a una variable de
// entorno la BORRA del proceso, y CRA volvería a leer el DSN real desde
// .env.production. Un build de dev reportando a Sentry ensucia el triage de
// producción con errores de código que todavía no llegó a main.
const sentryApagado = process.env.REACT_APP_SENTRY_DISABLED === "1";
if (sentryDsn && !sentryApagado && (isProdBuild || sendInDev)) {
  const tracesSampleRate = parseFloat(
    process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || "0.1",
  );
  Sentry.init({
    dsn: sentryDsn,
    environment: process.env.NODE_ENV || "development",
    tracesSampleRate: Number.isFinite(tracesSampleRate) ? tracesSampleRate : 0.1,
    sendDefaultPii: false,
    // Descarta el SyntaxError del chunk obsoleto tras un deploy (ver filtroSentry.js).
    beforeSend,
  });
} else if (sentryDsn && !isProdBuild) {
  // Visible a propósito: si alguien depura por qué un error local no aparece en
  // Sentry, la consola se lo dice en vez de dejarlo buscando.
  // eslint-disable-next-line no-console
  console.info(
    "[sentry] desactivado en desarrollo. REACT_APP_SENTRY_SEND_IN_DEV=1 para forzarlo.",
  );
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <div style={{ padding: "2rem", fontFamily: "sans-serif" }}>
          <h1>{i18n.t("errors.boundary.title")}</h1>
          <p>{i18n.t("errors.boundary.body")}</p>
          <pre style={{ color: "#a00", whiteSpace: "pre-wrap" }}>
            {error?.message || String(error)}
          </pre>
          <button
            type="button"
            onClick={resetError}
            style={{
              padding: "0.5rem 1rem",
              marginTop: "1rem",
              cursor: "pointer",
            }}
          >
            {i18n.t("errors.boundary.retry")}
          </button>
        </div>
      )}
    >
      <App />
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);
